import { el } from '../utils/dom.js';
import { parseTMJ } from '../core/TMJParser.js';

/**
 * GitHub dialog — connect PAT, select repo, save/load TMJ files.
 */
export class GitHubDialog {
  constructor(githubSync, state, fileManager, mapCanvas, compositor) {
    this._gh = githubSync;
    this._state = state;
    this._fileManager = fileManager;
    this._mapCanvas = mapCanvas;
    this._compositor = compositor;
  }

  /** Show the "Save to GitHub" dialog. */
  showSave() {
    if (!this._gh.isConnected) {
      this._showConnect('save');
      return;
    }
    this._showSaveDialog();
  }

  /** Show the "Load from GitHub" dialog. */
  showLoad() {
    if (!this._gh.isConnected) {
      this._showConnect('load');
      return;
    }
    this._showLoadDialog();
  }

  /** Show the connect dialog (PAT + repo selection). */
  _showConnect(andThen) {
    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog', style: { minWidth: '400px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Connect to GitHub'));

    // PAT input
    const patGroup = el('div', { className: 'form-group' });
    patGroup.appendChild(el('label', { className: 'form-label' }, 'Personal Access Token'));
    const patInput = el('input', {
      type: 'password',
      className: 'form-input',
      value: this._gh.token || '',
      placeholder: 'ghp_xxxxxxxxxxxx',
    });
    patGroup.appendChild(patInput);
    patGroup.appendChild(el('div', {
      style: { fontSize: '10px', color: 'var(--text-dim)', marginTop: '2px' },
    }, 'Needs "repo" scope. Create at github.com/settings/tokens'));
    dialog.appendChild(patGroup);

    // Repo selector (populated after PAT validated)
    const repoGroup = el('div', { className: 'form-group' });
    repoGroup.appendChild(el('label', { className: 'form-label' }, 'Repository'));
    const repoSelect = el('select', { className: 'form-input', disabled: true });
    repoSelect.appendChild(el('option', { value: '' }, 'Enter PAT and click Load Repos'));
    repoGroup.appendChild(repoSelect);
    dialog.appendChild(repoGroup);

    // Branch selector
    const branchGroup = el('div', { className: 'form-group' });
    branchGroup.appendChild(el('label', { className: 'form-label' }, 'Branch'));
    const branchSelect = el('select', { className: 'form-input', disabled: true });
    branchSelect.appendChild(el('option', { value: 'main' }, 'main'));
    branchGroup.appendChild(branchSelect);
    dialog.appendChild(branchGroup);

    // Path input
    const pathGroup = el('div', { className: 'form-group' });
    pathGroup.appendChild(el('label', { className: 'form-label' }, 'Path (directory in repo)'));
    const pathInput = el('input', { type: 'text', className: 'form-input', placeholder: 'maps/', value: '' });
    pathGroup.appendChild(pathInput);
    dialog.appendChild(pathGroup);

    // Status area
    const statusArea = el('div', { style: { fontSize: '11px', minHeight: '16px', marginBottom: '8px' } });
    dialog.appendChild(statusArea);

    let repos = [];

    // Load repos button
    const loadReposBtn = el('button', { className: 'btn btn-sm', style: { marginBottom: '12px' }, onClick: async () => {
      const pat = patInput.value.trim();
      if (!pat) { statusArea.textContent = 'Enter a PAT first.'; statusArea.style.color = 'var(--warning)'; return; }
      this._gh.setToken(pat);
      statusArea.textContent = 'Loading repos...';
      statusArea.style.color = 'var(--text-dim)';
      try {
        repos = await this._gh.listRepos();
        repoSelect.innerHTML = '';
        repoSelect.disabled = false;
        for (const r of repos) {
          repoSelect.appendChild(el('option', { value: r.full_name }, r.full_name));
        }
        statusArea.textContent = `Found ${repos.length} repos.`;
        statusArea.style.color = 'var(--success)';

        // Load branches for first repo
        if (repos.length > 0) {
          const r = repos[0];
          const branches = await this._gh.listBranches(r.owner, r.name);
          branchSelect.innerHTML = '';
          branchSelect.disabled = false;
          for (const b of branches) {
            branchSelect.appendChild(el('option', { value: b }, b));
          }
        }
      } catch (err) {
        statusArea.textContent = err.message;
        statusArea.style.color = 'var(--warning)';
      }
    }}, 'Load Repos');
    dialog.appendChild(loadReposBtn);

    // Update branches when repo changes
    repoSelect.addEventListener('change', async () => {
      const fullName = repoSelect.value;
      const repo = repos.find(r => r.full_name === fullName);
      if (!repo) return;
      try {
        const branches = await this._gh.listBranches(repo.owner, repo.name);
        branchSelect.innerHTML = '';
        branchSelect.disabled = false;
        for (const b of branches) {
          branchSelect.appendChild(el('option', { value: b }, b));
        }
      } catch { /* keep current branches */ }
    });

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const connectBtn = el('button', { className: 'btn btn-primary', onClick: () => {
      const fullName = repoSelect.value;
      const repo = repos.find(r => r.full_name === fullName);
      if (!repo) { statusArea.textContent = 'Select a repo.'; statusArea.style.color = 'var(--warning)'; return; }
      this._gh.setRepo(repo, branchSelect.value, pathInput.value.trim().replace(/\/$/, ''));
      overlay.remove();
      if (andThen === 'save') this._showSaveDialog();
      else if (andThen === 'load') this._showLoadDialog();
    }}, 'Connect');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(connectBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    patInput.focus();
  }

  _showSaveDialog() {
    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog', style: { minWidth: '380px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Save to GitHub'));

    const repo = this._gh.repo;
    dialog.appendChild(el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginBottom: '12px' },
    }, `${repo.full_name} / ${this._gh.branch}`));

    // File name
    const nameGroup = el('div', { className: 'form-group' });
    nameGroup.appendChild(el('label', { className: 'form-label' }, 'File Name'));
    const fileName = this._state.get('fileName') || 'untitled.tmj';
    const nameInput = el('input', { type: 'text', className: 'form-input', value: fileName });
    nameGroup.appendChild(nameInput);
    dialog.appendChild(nameGroup);

    // Path preview
    const pathPreview = el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' },
    });
    const updatePreview = () => {
      const p = this._gh.path ? `${this._gh.path}/${nameInput.value}` : nameInput.value;
      pathPreview.textContent = `Path: ${p}`;
    };
    nameInput.addEventListener('input', updatePreview);
    updatePreview();
    dialog.appendChild(pathPreview);

    const statusArea = el('div', { style: { fontSize: '11px', minHeight: '16px', marginTop: '8px' } });
    dialog.appendChild(statusArea);

    const btnRow = el('div', { className: 'dialog-buttons' });
    const saveBtn = el('button', { className: 'btn btn-primary', onClick: async () => {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving...';
      statusArea.textContent = 'Pushing to GitHub...';
      statusArea.style.color = 'var(--text-dim)';
      try {
        const p = this._gh.path ? `${this._gh.path}/${nameInput.value}` : nameInput.value;
        await this._gh.save(p);
        statusArea.textContent = 'Saved successfully!';
        statusArea.style.color = 'var(--success)';
        this._state.set('dirty', false);
        setTimeout(() => overlay.remove(), 1000);
      } catch (err) {
        statusArea.textContent = err.message;
        statusArea.style.color = 'var(--warning)';
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save';
      }
    }}, 'Save');
    const cancelBtn = el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Cancel');
    btnRow.append(saveBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  async _showLoadDialog() {
    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog', style: { minWidth: '400px' } });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Load from GitHub'));

    const repo = this._gh.repo;
    dialog.appendChild(el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginBottom: '12px' },
    }, `${repo.full_name} / ${this._gh.branch}`));

    const fileList = el('div', { className: 'github-file-list' });
    fileList.appendChild(el('div', { style: { color: 'var(--text-dim)', padding: '8px' } }, 'Loading...'));
    dialog.appendChild(fileList);

    const statusArea = el('div', { style: { fontSize: '11px', minHeight: '16px', marginTop: '8px' } });
    dialog.appendChild(statusArea);

    const btnRow = el('div', { className: 'dialog-buttons' });
    btnRow.appendChild(el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Close'));
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Load files
    try {
      const files = await this._gh.listFiles();
      fileList.innerHTML = '';

      const tmjFiles = files.filter(f => f.type === 'file' && /\.(tmj|json)$/i.test(f.name));
      const dirs = files.filter(f => f.type === 'dir');

      if (dirs.length === 0 && tmjFiles.length === 0) {
        fileList.appendChild(el('div', { style: { color: 'var(--text-dim)', padding: '8px' } }, 'No TMJ files found.'));
        return;
      }

      for (const dir of dirs) {
        const item = el('div', { className: 'github-file-item dir' }, `📁 ${dir.name}/`);
        // Browsing subdirectories is left for future enhancement
        fileList.appendChild(item);
      }

      for (const file of tmjFiles) {
        const item = el('div', {
          className: 'github-file-item',
          onClick: async () => {
            statusArea.textContent = `Loading ${file.name}...`;
            statusArea.style.color = 'var(--text-dim)';
            try {
              const data = await this._gh.load(file.path);
              const tmj = JSON.parse(data.content);
              const parsed = parseTMJ(tmj);
              if (!parsed.tilesetImages || !(parsed.tilesetImages instanceof Map)) {
                parsed.tilesetImages = new Map();
              }
              this._state.batch({
                ...parsed,
                fileName: data.name,
                dirty: false,
                scrollX: 0,
                scrollY: 0,
                zoom: 1,
                activeLayerIndex: 0,
              });
              this._mapCanvas.invalidateAll();
              this._compositor.requestRender();
              statusArea.textContent = `Loaded ${data.name}`;
              statusArea.style.color = 'var(--success)';
              setTimeout(() => overlay.remove(), 800);
            } catch (err) {
              statusArea.textContent = err.message;
              statusArea.style.color = 'var(--warning)';
            }
          },
        });
        item.appendChild(el('span', {}, file.name));
        item.appendChild(el('span', { style: { fontSize: '10px', color: 'var(--text-dim)', marginLeft: 'auto' } },
          file.size > 1024 ? `${(file.size / 1024).toFixed(1)} KB` : `${file.size} B`
        ));
        fileList.appendChild(item);
      }
    } catch (err) {
      fileList.innerHTML = '';
      fileList.appendChild(el('div', { style: { color: 'var(--warning)', padding: '8px' } }, err.message));
    }
  }
}
